import classNames from 'classnames';
import { Formik } from 'formik';
import uniqBy from 'lodash.uniqby';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// components
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import SelectCompanyRoster from '@atoms/SelectCompanyRoster';
import { ButtonOutlineLarge, ButtonOutlineSmall, ButtonPrimaryLarge, ButtonPrimarySmall, H4, LinkText } from '@styled';

// functionality
import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import useCartItemsGroupedByMember from '@hooks/useCartItemsGroupedByMember';
import { useScreenMinMd } from '@hooks/useResponsive';
import cartPropTypes from '@react/propTypes/cartPropTypes';
import useViewerCompanyNameEffective from '@hooks/useViewerCompanyNameEffective';

export const reasonsForExclusion = {
    ALREADY_IN_CART: 'in cart',
    ALREADY_REGISTERED: 'registered',
};

export const modalStyles = {
    body: {
        paddingBottom: '20px',
        paddingTop: '10px',
    },
    content: {
        maxWidth: 'auto',
        minWidth: 'auto',
        width: '594px',
    },
    overlay: {},
};

const BodyContainerStyled = styled.div`
    padding-left: 43px;
    padding-right: 43px;
`;

const ButtonContainerStyled = styled.div`
    border-top: 1px solid ${colors.lightGray};
    bottom: 20px;
    display: flex;
    justify-content: flex-end;
    margin-top: ${props => (props.limitToMemberId ? '38px' : '50px')};
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 20px;
    position: absolute;
    width: 100%;

    @media ${mediaQueries.md} {
        position: static;
    }
`;

const MemberCheckboxContainerStyled = styled.div`
    align-items: center;
    display: flex;

    &:not(:last-child) {
        margin-bottom: 1rem;
    }
`;

const CartUpsellModalBase = ({
    cart,
    coworkers,
    heading,
    limitToMemberId,
    membersToExclude,
    viewerId,
    onAddToCart,
    onCancelClick,
}) => {
    // ------------------------------
    // State
    // ------------------------------

    const [companyRoster, setCompanyRoster] = useState();
    const [isCoworkerOptionSelected, setIsCoworkerOptionSelected] = useState(false);
    const [isNewMemberOptionSelected, setIsNewMemberOptionSelected] = useState(false);
    const [selectedMembersFromCart, setSelectedMembersFromCart] = useState([]);
    const [selectedCoworkers, setSelectedCoworkers] = useState([]);
    const [showCoworkerDropdown, setShowCoworkerDropdown] = useState(true);
    const { viewerCompanyName } = useViewerCompanyNameEffective();

    // ------------------------------
    // Hooks
    // ------------------------------

    const { itemsGroupedByMember } = useCartItemsGroupedByMember({
        cart,
        itemCount: cart?.items?.length,
        total: cart?.total,
    });

    const isScreenMinMd = useScreenMinMd();

    // ------------------------------
    // Effects
    // ------------------------------

    useEffect(() => {
        if (!coworkers || !membersToExclude) {
            return;
        }

        // Hide coworker dropdown if only one person and should be excluded
        if (coworkers.length === 1 && membersToExclude.some(member => member.id === coworkers[0].value)) {
            setShowCoworkerDropdown(false);
        } else {
            setShowCoworkerDropdown(true);
        }

        setCompanyRoster(
            coworkers.map(coworker => {
                const excluded = membersToExclude.find(m => m.id === coworker.value);
                return {
                    value: coworker.value,
                    label: `${coworker.label}${excluded ? ` (${excluded.reason})` : ''}`,
                };
            })
        );
    }, [coworkers, membersToExclude]);

    // ------------------------------
    // Functions
    // ------------------------------

    const toggleMemberFromCart = ({ member, checked }) => {
        if (checked) {
            setSelectedMembersFromCart([
                ...selectedMembersFromCart,
                {
                    id: member.memberId,
                    name: member.memberName,
                },
            ]);
        } else {
            setSelectedMembersFromCart(selectedMembersFromCart.filter(m => m.id !== member.memberId));
        }
    };

    const updateCompanyRosterSelections = selectedOptions => {
        setSelectedCoworkers(
            selectedOptions.map(option => {
                const coworker = coworkers.find(c => c.value === option.value);
                return {
                    id: coworker.value,
                    name: coworker.label,
                };
            })
        );
    };

    const addToCart = () => {
        let selectedMembers = [...selectedMembersFromCart];
        if (isCoworkerOptionSelected) {
            selectedMembers = [...selectedMembers, ...selectedCoworkers];
        }

        onAddToCart({
            selectedMembers: uniqBy(selectedMembers, 'id'),
            shouldAddNewMember: isNewMemberOptionSelected,
        });
    };

    // ------------------------------
    // Render
    // ------------------------------

    if (!itemsGroupedByMember || !companyRoster) {
        return null;
    }

    const isAddButtonDisabled =
        (isCoworkerOptionSelected ? selectedCoworkers.length : 0) + selectedMembersFromCart.length === 0 &&
        !isNewMemberOptionSelected;

    return (
        <div>
            <BodyContainerStyled>
                <H4>{heading}</H4>

                <div className="mt-2rem">
                    {itemsGroupedByMember.map(member => {
                        const excluded = membersToExclude.map(m => m.id).includes(member.memberId);
                        const reasonForExclusion = membersToExclude.find(m => m.id === member.memberId)?.reason;
                        const isSelf = member.memberId === viewerId;

                        if (limitToMemberId && member.memberId !== limitToMemberId) {
                            return null;
                        }

                        return (
                            <MemberCheckboxContainerStyled key={member.memberId}>
                                <CheckboxWithoutForm
                                    checked={selectedMembersFromCart.map(m => m.id).includes(member.memberId)}
                                    disabled={excluded}
                                    onCheckChange={checked => toggleMemberFromCart({ member, checked })}
                                >
                                    <LinkText
                                        className={classNames({
                                            'text-light-gray': excluded,
                                        })}
                                    >
                                        {member.memberName}
                                        {excluded && isSelf && ` (me, ${reasonForExclusion})`}
                                        {excluded && !isSelf && ` (${reasonForExclusion})`}
                                        {!excluded && isSelf && ` (me)`}
                                    </LinkText>
                                </CheckboxWithoutForm>
                            </MemberCheckboxContainerStyled>
                        );
                    })}
                </div>

                {!limitToMemberId && (
                    <>
                        {companyRoster.length > 0 && viewerCompanyName && showCoworkerDropdown && (
                            <div className="mt-1rem">
                                <CheckboxWithoutForm
                                    checked={isCoworkerOptionSelected}
                                    onCheckChange={checked => setIsCoworkerOptionSelected(checked)}
                                >
                                    <LinkText>Another co-worker:</LinkText>
                                </CheckboxWithoutForm>

                                <div className="ml-1.75rem">
                                    <Formik>
                                        <SelectCompanyRoster
                                            companyName={viewerCompanyName}
                                            name="selectedCoworkers"
                                            options={companyRoster}
                                            isDisabled={!isCoworkerOptionSelected}
                                            isOptionDisabled={option =>
                                                membersToExclude.map(m => m.id).includes(option.value)
                                            }
                                            onChange={selectedOptions => updateCompanyRosterSelections(selectedOptions)}
                                            placeholder="Select from company roster"
                                        />
                                    </Formik>
                                </div>
                            </div>
                        )}

                        <div className="mt-1rem">
                            <CheckboxWithoutForm
                                checked={isNewMemberOptionSelected}
                                onCheckChange={checked => setIsNewMemberOptionSelected(checked)}
                            >
                                <LinkText>Add Someone New</LinkText>
                            </CheckboxWithoutForm>
                        </div>
                    </>
                )}
            </BodyContainerStyled>

            {isScreenMinMd && (
                <ButtonContainerStyled limitToMemberId={!!limitToMemberId}>
                    <ButtonOutlineLarge onClick={onCancelClick}>Cancel</ButtonOutlineLarge>
                    <ButtonPrimaryLarge className="ml-1rem" disabled={isAddButtonDisabled} onClick={() => addToCart()}>
                        Add to Cart
                    </ButtonPrimaryLarge>
                </ButtonContainerStyled>
            )}

            {!isScreenMinMd && (
                <ButtonContainerStyled limitToMemberId={!!limitToMemberId}>
                    <ButtonOutlineSmall onClick={onCancelClick}>Cancel</ButtonOutlineSmall>
                    <ButtonPrimarySmall className="ml-1rem" disabled={isAddButtonDisabled} onClick={() => addToCart()}>
                        Add to Cart
                    </ButtonPrimarySmall>
                </ButtonContainerStyled>
            )}
        </div>
    );
};

CartUpsellModalBase.propTypes = {
    cart: cartPropTypes,
    coworkers: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ),
    heading: PropTypes.string.isRequired,
    limitToMemberId: PropTypes.number,
    membersToExclude: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            reason: PropTypes.oneOf(Object.values(reasonsForExclusion)).isRequired,
        })
    ),
    viewerId: PropTypes.number.isRequired,
    onAddToCart: PropTypes.func.isRequired,
    onCancelClick: PropTypes.func.isRequired,
};

export default CartUpsellModalBase;
